import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertClubSchema, type InsertClub } from "../../../shared/validators";
import type { ClubWithMembership, Player, ClubRanking, ClubMember } from "../../../shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Plus, Users, Trophy, Calendar, Search, Building2, Star, Trash2, UserMinus, Shield, Edit, UserPlus, Crown, Minus, BadgeCheck, Share2, X, Check } from "lucide-react";
import { validateInput } from "@/lib/inputValidation";
import InputRestrictionsDialog from "@/components/input-restrictions-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ResponsiveModal } from "@/components/responsive-modal";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ClubsTabProps {
  selectedPlayerId?: string | null;
  initialEditClubId?: string | null;
  onClearEditClubId?: () => void;
  initialSearchQuery?: string;
}

export default function ClubsTab({ selectedPlayerId, initialEditClubId, onClearEditClubId, initialSearchQuery }: ClubsTabProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedClub, setSelectedClub] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClub, setEditingClub] = useState<ClubWithMembership | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || "");
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);
  const [pendingPlayerIds, setPendingPlayerIds] = useState<Set<string>>(new Set());
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  const [leaveClubDialogOpen, setLeaveClubDialogOpen] = useState(false);
  const [clubToLeave, setClubToLeave] = useState<string | null>(null);
  const [showRestrictionsDialog, setShowRestrictionsDialog] = useState(false);
  const [restrictionMessage, setRestrictionMessage] = useState("");
  const [confirmRemovalDialogOpen, setConfirmRemovalDialogOpen] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<InsertClub | null>(null);
  const [originalMemberIds, setOriginalMemberIds] = useState<string[]>([]);
  const [originalActiveMemberIds, setOriginalActiveMemberIds] = useState<string[]>([]);
  const [resetSeasonConfirmOpen, setResetSeasonConfirmOpen] = useState(false);
  const [clubToResetSeason, setClubToResetSeason] = useState<string | null>(null);
  const [isTourActive, setIsTourActive] = useState(false);

  useEffect(() => {
    const handleTourStatus = (e: any) => {
      if (e.detail?.tab === 'clubs') {
        setIsTourActive(e.detail.isRunning);
      } else {
        setIsTourActive(false);
      }
    };

    window.addEventListener('padelo-tour-status', handleTourStatus);
    return () => window.removeEventListener('padelo-tour-status', handleTourStatus);
  }, []);

  const editClubForm = useForm<InsertClub>({
    resolver: zodResolver(insertClubSchema),
    defaultValues: {
      name: "",
      description: "",
      validationMethod: "standard",
      newPlayerValidation: "auto",
      eloStarting: "standard",
    },
  });

  // Get followed players from localStorage for sorting
  const followedPlayers = new Set(JSON.parse(localStorage.getItem("followedPlayers") || "[]"));

  const { data: clubs = [], isLoading: clubsLoading } = useQuery<ClubWithMembership[]>({
    queryKey: ["/api/clubs", selectedPlayerId],
    queryFn: async () => {
      const url = selectedPlayerId ? `/api/clubs?playerId=${selectedPlayerId}` : '/api/clubs';
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  // Keep track of whether we opened the club specifically for the tour
  const [openedByTour, setOpenedByTour] = useState(false);

  // Effect to automatically open the target club when the tour runs
  useEffect(() => {
    if (isTourActive && clubs.length > 0 && !selectedClub) {
      // Try to find "Mouratoglou Bro" or fallback to the first club
      const targetClub = clubs.find(c => c.name.toLowerCase().includes('mouratoglou')) || clubs[0];
      setSelectedClub(targetClub.id);
      setOpenedByTour(true);
    } else if (!isTourActive && openedByTour) {
      // Close the club that was automatically opened when the tour ends
      setSelectedClub(null);
      setOpenedByTour(false);
    }
  }, [isTourActive, clubs, selectedClub, openedByTour]);

  // Count clubs created by the current user
  const userCreatedClubs = clubs.filter(club => club.isAdmin).length;
  const maxClubsPerUser = 5;
  const canCreateMoreClubs = userCreatedClubs < maxClubsPerUser;

  const { data: players = [] } = useQuery<Player[]>({
    queryKey: ["/api/players"],
  });

  // Fetch current player data to check premium status
  const { data: currentPlayer } = useQuery<Player>({
    queryKey: ["/api/players", selectedPlayerId],
    queryFn: async () => {
      if (!selectedPlayerId) return null;
      const response = await apiRequest("GET", `/api/players/${selectedPlayerId}`);
      return response.json();
    },
    enabled: !!selectedPlayerId,
  });

  const ClubRankings = ({ clubId }: { clubId: string }) => {
    const [showOthers, setShowOthers] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState("current");

    const currentClub = clubs.find(c => c.id === clubId);
    const archivedSeasons = currentClub?.archivedSeasons || [];

    const { data: currentRankings = [], isLoading } = useQuery<ClubRanking[]>({
      queryKey: ["/api/clubs", clubId, "rankings", "all"],
      queryFn: async () => {
        const response = await apiRequest("GET", `/api/clubs/${clubId}/rankings?timeFilter=all`);
        return response.json();
      },
      enabled: selectedClub === clubId && selectedSeason === "current", // Only fetch when this club is selected and current season
    });

    let displayedRankings: any[] = [];
    if (selectedSeason === "current") {
      displayedRankings = currentRankings;
    } else {
      const archive = archivedSeasons.find((a: any) => a.id === selectedSeason);
      if (archive && archive.rankings) {
        // Archive already stores camelCase fields (clubEloRating, clubMatchesWon, etc.)
        // Just normalize the id field which the UI expects
        displayedRankings = archive.rankings.map((r: any) => ({
          ...r,
          id: r.playerId || r.id,
          name: r.name || r.playerName,
        }));
      }
    }

    if (isLoading && selectedSeason === "current") {
      return <div className="text-center p-2 text-sm text-muted-foreground">{t('common.loading')}</div>;
    }

    if (displayedRankings.length === 0) {
      return (
        <div className="text-center p-2 text-sm text-muted-foreground">
          {t('clubs.messages.noMatches')}
        </div>
      );
    }

    const podiumPlayers = displayedRankings.slice(0, 3);
    const otherPlayers = displayedRankings.slice(3);

    return (
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-border" id="tour-clubs-podium">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-muted-foreground flex items-center">
            <Trophy className="w-4 h-4 mr-1" />
            {selectedSeason === "current" ? t('ranking.title') : t('ranking.season.title')}
          </h4>

          {archivedSeasons.length > 0 && (
            <Select value={selectedSeason} onValueChange={setSelectedSeason}>
              <SelectTrigger className="w-[180px] h-8 text-xs bg-white dark:bg-card">
                <SelectValue placeholder={t('season.title')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current" className="text-xs font-medium">
                  {t('season.current', { defaultValue: 'Saison en cours' })}
                </SelectItem>
                {[...archivedSeasons].sort((a: any, b: any) => {
                  // Sort by date prefix descending (e.g. 202603 before 202509)
                  const aDate = a.name?.split(' - ')[0] || '';
                  const bDate = b.name?.split(' - ')[0] || '';
                  return bDate.localeCompare(aDate);
                }).map((season: any) => (
                  <SelectItem key={season.id} value={season.id} className="text-xs">
                    {season.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Podium Code */}
        {podiumPlayers.length > 0 && (
          <div className="flex justify-center items-end h-40 mb-6 gap-2 sm:gap-4 mt-8">
            {/* 2nd Place */}
            {podiumPlayers[1] && (
              <div className="flex flex-col items-center w-24 sm:w-28 relative animate-in slide-in-from-bottom-4 duration-500 delay-150">
                <div className="absolute -top-10 sm:-top-12 flex flex-col items-center">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 dark:bg-gray-600 border-2 border-gray-100 dark:border-gray-500 shadow-md flex items-center justify-center -mb-2 z-10">
                    <span className="text-gray-600 dark:text-gray-100 font-bold text-sm">2</span>
                  </div>
                  <div className="bg-card dark:bg-gray-700 px-2 py-1 rounded-md shadow-sm border border-border dark:border-gray-500 text-center w-20 sm:w-24">
                    <p className="text-xs font-semibold truncate w-full text-foreground dark:text-gray-50">{podiumPlayers[1].name || podiumPlayers[1].playerName}</p>
                    <p className="text-[10px] text-blue-600 dark:text-gray-200 font-bold">{podiumPlayers[1].clubEloRating} ELO</p>
                  </div>
                </div>
                <div className="w-full h-20 bg-gradient-to-t from-gray-300 to-gray-100 rounded-t-lg shadow-inner border-t-2 border-gray-300 flex items-end justify-center pb-2 dark:from-gray-700 dark:to-gray-600 dark:border-gray-600">
                  <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap">
                    {podiumPlayers[1].clubMatchesWon}W - {podiumPlayers[1].clubMatchesLost}L
                  </span>
                </div>
              </div>
            )}

            {/* 1st Place */}
            {podiumPlayers[0] && (
              <div className="flex flex-col items-center w-24 sm:w-32 relative animate-in slide-in-from-bottom-8 duration-700 z-10">
                <div className="absolute -top-14 sm:-top-16 flex flex-col items-center">
                  <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 mb-1 drop-shadow-md animate-bounce" />
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-yellow-100 border-2 border-white shadow-lg flex items-center justify-center -mb-2 z-10 relative">
                    <div className="absolute inset-0 bg-yellow-400 rounded-full animate-pulse opacity-50"></div>
                    <span className="text-yellow-700 font-bold text-base relative z-10">1</span>
                  </div>
                  <div className="bg-card dark:bg-yellow-700/50 px-2 py-1.5 rounded-md shadow-md border border-yellow-200 dark:border-yellow-500/60 text-center w-24 sm:w-28 relative z-20">
                    <p className="text-xs font-bold truncate w-full text-foreground dark:text-yellow-50">{podiumPlayers[0].name || podiumPlayers[0].playerName}</p>
                    <p className="text-[11px] text-blue-600 dark:text-yellow-200 font-black">{podiumPlayers[0].clubEloRating} ELO</p>
                  </div>
                </div>
                <div className="w-full h-28 bg-gradient-to-t from-yellow-300 via-yellow-200 to-yellow-100 rounded-t-lg shadow-inner border-t-4 border-yellow-400 flex items-end justify-center pb-2 relative dark:from-yellow-700 dark:via-yellow-600 dark:to-yellow-500 dark:border-yellow-500">
                  <span className="text-xs text-yellow-800 dark:text-yellow-100 font-semibold bg-white/50 dark:bg-black/30 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {podiumPlayers[0].clubMatchesWon}W - {podiumPlayers[0].clubMatchesLost}L
                  </span>
                </div>
              </div>
            )}

            {/* 3rd Place */}
            {podiumPlayers[2] && (
              <div className="flex flex-col items-center w-24 sm:w-28 relative animate-in slide-in-from-bottom-2 duration-500 delay-300">
                <div className="absolute -top-10 sm:-top-12 flex flex-col items-center">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-100 dark:bg-orange-700 border-2 border-white dark:border-orange-500 shadow-md flex items-center justify-center -mb-2 z-10">
                    <span className="text-orange-600 dark:text-orange-100 font-bold text-sm">3</span>
                  </div>
                  <div className="bg-card dark:bg-orange-700 px-2 py-1 rounded-md shadow-sm border border-orange-50 dark:border-orange-500 text-center w-20 sm:w-24">
                    <p className="text-xs font-semibold truncate w-full text-foreground dark:text-orange-50">{podiumPlayers[2].name || podiumPlayers[2].playerName}</p>
                    <p className="text-[10px] text-blue-600 dark:text-orange-200 font-bold">{podiumPlayers[2].clubEloRating} ELO</p>
                  </div>
                </div>
                <div className="w-full h-16 bg-gradient-to-t from-orange-200 to-orange-50 rounded-t-lg shadow-inner border-t-2 border-orange-200 flex items-end justify-center pb-2 dark:from-orange-700 dark:to-orange-600 dark:border-orange-600">
                  <span className="text-[10px] text-orange-800 dark:text-orange-100 font-medium whitespace-nowrap">
                    {podiumPlayers[2].clubMatchesWon}W - {podiumPlayers[2].clubMatchesLost}L
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Other players collapsible */}
        {otherPlayers.length > 0 && (
          <div className="space-y-4">
            <Button
              variant="outline"
              className="w-full flex justify-between items-center text-sm font-medium text-muted-foreground border-border"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowOthers(!showOthers);
              }}
              data-testid="button-see-others"
            >
              <span>{t('ranking.season.seeOthers', { defaultValue: 'Voir les autres joueurs' })} ({otherPlayers.length})</span>
              <Minus className={`w-4 h-4 transition-transform duration-200 ${showOthers ? 'rotate-180' : ''}`} />
            </Button>

            {showOthers && (
              <div className="space-y-1 animate-in slide-in-from-top-2 duration-300">
                {otherPlayers.map((player: any, index: number) => {
                  const currentClub = clubs.find(c => c.id === clubId);
                  const isPlayerAdmin = (currentClub && player.userId === currentClub.createdById) || player.isAdmin;

                  return (
                    <div
                      key={player.id}
                      className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-muted text-sm"
                      data-testid={`ranking-${player.id}`}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="w-5 h-5 rounded-full bg-padel-green text-white text-xs flex items-center justify-center">
                          {index + 4}
                        </span>
                        <span className="font-medium">{player.name || player.playerName}</span>
                        {isPlayerAdmin && selectedSeason === "current" && (
                          <div className="flex items-center space-x-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                            <Shield className="w-3 h-3" />
                            <span>Admin</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div className={`font-medium ${player.clubEloRating >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          ELO: {player.clubEloRating}
                        </div>
                        <div>{player.clubMatchesWon}W-{player.clubMatchesLost}L</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const createClubForm = useForm<InsertClub>({
    resolver: zodResolver(insertClubSchema),
    defaultValues: {
      name: "",
      description: "",
      validationMethod: "standard",
      newPlayerValidation: "auto",
      eloStarting: "standard",
    },
  });

  const createClubMutation = useMutation({
    mutationFn: async (data: InsertClub) => {
      const token = localStorage.getItem('authToken');
      const response = await apiRequest("POST", "/api/clubs", data, {
        'Authorization': `Bearer ${token}`
      });
      const club = await response.json();

      // Add selected players to the club if any
      if (selectedPlayers.length > 0) {
        await apiRequest("PUT", `/api/clubs/${club.id}/members`, {
          playerIds: selectedPlayers,
          adminIds: selectedAdmins
        }, {
          'Authorization': `Bearer ${token}`
        });
      }

      return club;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      setCreateDialogOpen(false);
      createClubForm.reset();
      setSelectedPlayers([]);
      setSelectedAdmins([]);
      toast({ description: t('clubs.create.success') });
    },
    onError: (error) => {
      console.error("Club creation error:", error);
      toast({ description: t('clubs.create.error'), variant: "destructive" });
    },
  });

  const resetSeasonMutation = useMutation({
    mutationFn: async (clubId: string) => {
      const token = localStorage.getItem('authToken');
      const response = await apiRequest("POST", `/api/clubs/${clubId}/reset`, undefined, {
        'Authorization': `Bearer ${token}`
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      setEditDialogOpen(false);
      setEditingClub(null);
      toast({ description: t('clubs.messages.seasonResetSuccess', { defaultValue: 'Season reset successfully!' }) });
    },
    onError: (error) => {
      console.error("Season reset error:", error);
      toast({ description: t('clubs.messages.seasonResetError', { defaultValue: 'Failed to reset season' }), variant: "destructive" });
    },
  });

  const joinClubMutation = useMutation({
    mutationFn: async ({ clubId, playerId }: { clubId: string; playerId: string }) => {
      const response = await apiRequest("POST", `/api/clubs/${clubId}/join`, { playerId });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      if (data.status === 'pending') {
        toast({ description: "Join request sent. Waiting for admin approval.", duration: 5000 });
      } else {
        toast({ description: "Joined club successfully!" });
      }
    },
    onError: (error) => {
      console.error("Join club error:", error);
      toast({ description: "Failed to join club", variant: "destructive" });
    },
  });

  const leaveClubMutation = useMutation({
    mutationFn: async ({ clubId, playerId }: { clubId: string; playerId: string }) => {
      const response = await apiRequest("DELETE", `/api/clubs/${clubId}/leave`, { playerId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      toast({ description: "Left club successfully!" });
    },
    onError: (error) => {
      console.error("Leave club error:", error);
      toast({ description: "Failed to leave club", variant: "destructive" });
    },
  });







  const deleteClubMutation = useMutation({
    mutationFn: async (clubId: string) => {
      const token = localStorage.getItem('authToken');
      const response = await apiRequest("DELETE", `/api/clubs/${clubId}`, null, {
        'Authorization': `Bearer ${token}`
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      setSelectedClub(null);
      setEditDialogOpen(false);
      setEditingClub(null);
      setSelectedPlayers([]);
      editClubForm.reset();
      toast({ description: "Club deleted successfully!" });
    },
    onError: (error) => {
      console.error("Delete club error:", error);
      toast({ description: "Failed to delete club", variant: "destructive" });
    },
  });

  const removePlayerMutation = useMutation({
    mutationFn: async ({ clubId, playerId }: { clubId: string; playerId: string }) => {
      const token = localStorage.getItem('authToken');
      const response = await apiRequest("DELETE", `/api/clubs/${clubId}/members/${playerId}`, null, {
        'Authorization': `Bearer ${token}`
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clubs", selectedClub, "rankings"] });
      toast({ description: "Player removed from club successfully!" });
    },
    onError: (error) => {
      console.error("Remove player error:", error);
      toast({ description: "Failed to remove player from club", variant: "destructive" });
    },
  });

  const updateClubMutation = useMutation({
    mutationFn: async ({ clubId, data, playerIds, adminIds }: { clubId: string; data: InsertClub; playerIds: string[]; adminIds: string[] }) => {
      const token = localStorage.getItem('authToken');

      // Update club details
      const response = await apiRequest("PUT", `/api/clubs/${clubId}`, data, {
        'Authorization': `Bearer ${token}`
      });
      const club = await response.json();

      // Update club members and admins
      await apiRequest("PUT", `/api/clubs/${clubId}/members`, { playerIds, adminIds }, {
        'Authorization': `Bearer ${token}`
      });

      return club;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clubs"] });
      setEditDialogOpen(false);
      setEditingClub(null);
      setSelectedPlayers([]);
      setSelectedAdmins([]);
      setPlayerSearchQuery("");
      editClubForm.reset();
      toast({ description: t('clubs.edit.success') });
    },
    onError: (error) => {
      console.error("Update club error:", error);
      toast({ description: t('clubs.edit.error'), variant: "destructive" });
    },
  });

  const onCreateClub = createClubForm.handleSubmit((data) => {
    // Validate club name
    const nameValidation = validateInput(data.name, "club name");
    if (!nameValidation.isValid) {
      setRestrictionMessage(nameValidation.error || "Invalid club name");
      setShowRestrictionsDialog(true);
      return;
    }

    // Validate club description if provided
    if (data.description && data.description.trim()) {
      const descriptionValidation = validateInput(data.description, "club description");
      if (!descriptionValidation.isValid) {
        setRestrictionMessage(descriptionValidation.error || "Invalid club description");
        setShowRestrictionsDialog(true);
        return;
      }
    }

    createClubMutation.mutate(data);
  });

  const handleJoinClub = (clubId: string) => {
    // Check if user is premium
    if (!currentPlayer?.isPremium) {
      showPremiumRestriction("Joining");
      return;
    }

    if (!selectedPlayerId) {
      toast({ description: "Please select a player first", variant: "destructive" });
      return;
    }
    joinClubMutation.mutate({ clubId, playerId: selectedPlayerId });
  };

  const handleLeaveClub = async (clubId: string) => {
    // Check if user is premium
    if (!currentPlayer?.isPremium) {
      showPremiumRestriction("Leaving");
      return;
    }

    if (!selectedPlayerId) {
      toast({ description: "Please select a player first", variant: "destructive" });
      return;
    }

    // Check if user is the last admin
    const club = clubs.find(c => c.id === clubId);
    if (club?.isAdmin) {
      try {
        // Fetch club members to check if user is the only admin
        const response = await apiRequest("GET", `/api/clubs/${clubId}/members`);
        const members = await response.json();

        // Count current admins (including creator and designated admins)
        const adminCount = members.filter((m: ClubMember) => m.isAdmin || m.userId === club.createdById).length;

        if (adminCount <= 1) {
          toast({
            description: t('clubs.messages.adminWarning'),
            variant: "destructive"
          });
          return;
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
      }
    }

    // Show confirmation dialog
    setClubToLeave(clubId);
    setLeaveClubDialogOpen(true);
  };

  const confirmLeaveClub = () => {
    if (clubToLeave && selectedPlayerId) {
      leaveClubMutation.mutate({ clubId: clubToLeave, playerId: selectedPlayerId });
      setLeaveClubDialogOpen(false);
      setClubToLeave(null);
    }
  };

  const showPremiumRestriction = (action: string) => {
    toast({
      title: (
        <div className="flex items-center space-x-2">
          <span>Premium Feature</span>
          <Crown className="w-4 h-4 text-yellow-500" />
        </div>
      ),
      description: t('clubs.messages.premiumDesc', { action }),
      variant: "default",
    });
  };

  const handleCreateClubClick = () => {
    // Check if user is premium
    if (!currentPlayer?.isPremium) {
      showPremiumRestriction("Creating");
      return;
    }

    if (!canCreateMoreClubs) {
      toast({
        description: t('clubs.messages.limitReached', { max: maxClubsPerUser }),
        variant: "destructive"
      });
      return;
    }
    setCreateDialogOpen(true);
  };

  const handleEditClub = async (club: ClubWithMembership) => {
    // Check if user is premium
    if (!currentPlayer?.isPremium) {
      showPremiumRestriction("Editing");
      return;
    }

    setEditingClub(club);
    // Pre-populate form with current club data
    editClubForm.reset({
      name: club.name,
      description: club.description || "",
      validationMethod: club.validationMethod || "standard",
      newPlayerValidation: club.newPlayerValidation || "auto",
      eloStarting: club.eloStarting || "standard",
    });

    // Fetch current members to ensure we have the latest data
    try {
      const response = await apiRequest("GET", `/api/clubs/${club.id}/members`);
      const members = await response.json();
      setSelectedPlayers(members.map((m: ClubMember) => m.id));

      // Identify pending members
      const pending = new Set(members.filter((m: ClubMember) => m.status === 'pending').map((m: ClubMember) => m.id));
      setPendingPlayerIds(pending);

      // Set current admins
      setSelectedAdmins(members.filter((m: ClubMember) => m.isAdmin).map((m: ClubMember) => m.id));
      setPlayerSearchQuery("");
      setPlayerSearchQuery("");
      setOriginalMemberIds(members.map((m: ClubMember) => m.id));
      // Store active members for removal warning
      setOriginalActiveMemberIds(members.filter((m: ClubMember) => m.status === 'active' || !m.status).map((m: ClubMember) => m.id));
    } catch (error) {
      console.error("Failed to fetch club members:", error);
      // Fallback to cached data if available
      const memberIds = club.currentMembers?.map(m => m.id) || [];
      const activeMemberIds = club.currentMembers?.filter(m => m.status === 'active' || !m.status).map(m => m.id) || [];
      setSelectedPlayers(memberIds);
      setOriginalMemberIds(memberIds);
      setOriginalActiveMemberIds(activeMemberIds);
      setSelectedAdmins([]);
      setPlayerSearchQuery("");
    }

    setEditDialogOpen(true);
  };

  const onEditClub = editClubForm.handleSubmit((data) => {
    // Validate club name
    const nameValidation = validateInput(data.name, "club name");
    if (!nameValidation.isValid) {
      setRestrictionMessage(nameValidation.error || "Invalid club name");
      setShowRestrictionsDialog(true);
      return;
    }

    // Validate club description if provided
    if (data.description && data.description.trim()) {
      const descriptionValidation = validateInput(data.description, "club description");
      if (!descriptionValidation.isValid) {
        setRestrictionMessage(descriptionValidation.error || "Invalid club description");
        setShowRestrictionsDialog(true);
        return;
      }
    }

    if (editingClub) {
      // Check if any existing ACTIVE members are being removed
      // We use originalActiveMemberIds instead of originalMemberIds to avoid warning when rejecting pending members
      const removedPlayers = originalActiveMemberIds.filter(id => !selectedPlayers.includes(id));

      if (removedPlayers.length > 0) {
        setPendingEditData(data);
        setConfirmRemovalDialogOpen(true);
        return;
      }

      updateClubMutation.mutate({
        clubId: editingClub.id,
        data,
        playerIds: selectedPlayers,
        adminIds: selectedAdmins
      });
    }
  });

  const handleConfirmRemoval = () => {
    if (editingClub && pendingEditData) {
      updateClubMutation.mutate({
        clubId: editingClub.id,
        data: pendingEditData,
        playerIds: selectedPlayers,
        adminIds: selectedAdmins
      });
      setConfirmRemovalDialogOpen(false);
      setPendingEditData(null);
    }
  };



  // Handle deep linking to edit club dialog
  useEffect(() => {
    if (initialEditClubId && clubs && clubs.length > 0 && !editDialogOpen && !editingClub) {
      const clubToEdit = clubs.find(c => c.id === initialEditClubId);
      if (clubToEdit) {
        // Only allow editing if user is admin
        if (clubToEdit.isAdmin) {
          handleEditClub(clubToEdit);
          if (onClearEditClubId) {
            onClearEditClubId();
          }
        }
      }
    }
  }, [initialEditClubId, clubs, editDialogOpen, editingClub, onClearEditClubId]);

  const handleClubClick = (clubId: string) => {
    setSelectedClub(selectedClub === clubId ? null : clubId);
  };

  const handleShareClub = async (club: ClubWithMembership, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the club accordion
    const url = (club as any).slug
      ? `https://app.padelo.fr/join/${(club as any).slug}`
      : `https://app.padelo.fr/clubs?search=${encodeURIComponent(club.name)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Rejoignez ${club.name} sur PadELO`,
          text: `Retrouvez le club ${club.name} sur l'application PadELO !`,
          url: url,
        });
      } catch (error) {
        console.log('Erreur de partage:', error);
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast({
          description: t('clubs.messages.shareUrlCopied', { defaultValue: 'Lien du club copié dans le presse-papiers !' }),
        });
      } catch (error) {
        console.error('Failed to copy list link:', error);
        toast({
          description: "Impossible de copier le lien",
          variant: "destructive",
        });
      }
    }
  };

  // Filter and sort clubs based on search query and membership status
  const filteredClubs = (clubs as ClubWithMembership[])
    .filter(club => {
      if (!searchQuery) return true;

      // Split search query by pipe, comma, or semicolon to allow multiple OR terms
      const searchGroups = searchQuery.split(/[|;,]/).map(t => t.trim().toLowerCase()).filter(t => t.length > 0);

      if (searchGroups.length === 0) return true;

      const clubName = club.name.toLowerCase();
      const clubDesc = club.description ? club.description.toLowerCase() : "";

      // A club matches if it satisfies AT LEAST ONE group (OR logic)
      return searchGroups.some(group => {
        // Within a group, split by spaces or + for AND logic
        const requiredWords = group.split(/[\s+]+/).filter(w => w.length > 0);

        // It must contain ALL required words in either name or description
        return requiredWords.every(word => clubName.includes(word) || clubDesc.includes(word));
      });
    })
    .sort((a, b) => {
      // First, sort by membership status (joined clubs first)
      if (a.isMember && !b.isMember) return -1;
      if (!a.isMember && b.isMember) return 1;

      // Then sort alphabetically by name
      return a.name.localeCompare(b.name);
    });

  // Sort and filter players based on search query
  const sortedPlayers = players
    .filter(player =>
      player.name.toLowerCase().includes(playerSearchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // First, sort by followed status (followed players first)
      const aFollowed = followedPlayers.has(a.id);
      const bFollowed = followedPlayers.has(b.id);

      if (aFollowed && !bFollowed) return -1;
      if (!aFollowed && bFollowed) return 1;

      // Then sort alphabetically by name
      return a.name.localeCompare(b.name);
    });

  const rejectPendingPlayer = (playerId: string) => {
    const newPending = new Set(pendingPlayerIds);
    newPending.delete(playerId);
    setPendingPlayerIds(newPending);
    setSelectedPlayers(prev => prev.filter(id => id !== playerId));
  };

  const togglePlayerSelection = (playerId: string) => {
    // Check if player is pending validation
    if (pendingPlayerIds.has(playerId)) {
      // Validate player: Remove from pending (so it becomes normal checked), keep in selected
      const newPending = new Set(pendingPlayerIds);
      newPending.delete(playerId);
      setPendingPlayerIds(newPending);
      // Ensure it stays selected
      if (!selectedPlayers.includes(playerId)) {
        setSelectedPlayers(prev => [...prev, playerId]);
      }
      return;
    }

    setSelectedPlayers(prev => {
      const isSelected = prev.includes(playerId);
      if (isSelected) {
        // If deselecting player, also remove from admins
        setSelectedAdmins(adminPrev => adminPrev.filter(id => id !== playerId));
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  const toggleAdminSelection = (playerId: string) => {
    setSelectedAdmins(prev =>
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  if (clubsLoading) {
    return <div className="flex justify-center p-8">{t('common.loading')}</div>;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Search Bar + Add Button */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" size={18} />
          <Input
            type="text"
            placeholder={t('clubs.form.searchClubs')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-8 bg-gray-50 dark:bg-muted border-transparent focus-visible:ring-1 focus-visible:bg-white dark:focus-visible:bg-card text-xs placeholder:text-gray-400 dark:placeholder:text-muted-foreground h-9"
            data-testid="input-search-clubs"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <Button
          id="tour-clubs-create"
          data-testid="button-create-club"
          disabled={!canCreateMoreClubs}
          onClick={handleCreateClubClick}
          className={`h-9 px-3 shrink-0 ${
            canCreateMoreClubs
              ? 'bg-padel-green hover:bg-padel-green-light text-white dark-btn-active'
              : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
          }`}
        >
          <Plus className="w-4 h-4" />
          {!canCreateMoreClubs && <span className="ml-1 text-xs">({userCreatedClubs}/{maxClubsPerUser})</span>}
        </Button>
      </div>

      <ResponsiveModal
          open={createDialogOpen}
          onOpenChange={(open) => {
            setCreateDialogOpen(open);
            if (!open) {
              // Clear selected players and admins when dialog closes
              setSelectedPlayers([]);
              setSelectedAdmins([]);
              setPlayerSearchQuery("");
              createClubForm.reset();
            } else if (selectedPlayerId) {
              // Auto-select the current user as admin when creating a club
              setSelectedPlayers([selectedPlayerId]);
              setSelectedAdmins([selectedPlayerId]);
            }
          }}
          title={
            <div className="flex items-center gap-3">
              <span>{t('clubs.create.title')}</span>
              {userCreatedClubs > 0 && (
                <div className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-xs font-normal text-blue-800">
                  {t('clubs.messages.createdCount', { current: userCreatedClubs, max: maxClubsPerUser })}
                </div>
              )}
            </div>
          }
        >
          <Form {...createClubForm}>
            <form onSubmit={onCreateClub} className="flex flex-col max-h-[calc(100vh-10rem)] sm:max-h-[75vh]">
              <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 pb-2 space-y-4">
                <Accordion type="single" collapsible defaultValue="general" className="w-full space-y-4">
                  <AccordionItem value="general" className="bg-white dark:bg-card border border-gray-200 dark:border-border shadow-sm rounded-xl overflow-hidden">
                    <AccordionTrigger className="bg-gray-50/80 dark:bg-transparent hover:bg-gray-100 dark:hover:bg-muted/30 hover:no-underline px-4 py-3 text-base font-semibold text-gray-800 dark:text-foreground data-[state=open]:border-b data-[state=open]:dark:bg-muted/40 border-gray-100 dark:border-border transition-colors m-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        Informations Générales
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 p-4 bg-white dark:bg-card">
                      <FormField
                        control={createClubForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel className="text-muted-foreground font-medium">{t('clubs.form.name')}</FormLabel>
                            <FormControl>
                              <Input
                                data-testid="input-club-name"
                                placeholder={t('clubs.form.namePlaceholder')}
                                maxLength={25}
                                className="bg-gray-50/80 dark:bg-input border-gray-200 dark:border-border focus:bg-white transition-colors"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createClubForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel className="text-muted-foreground font-medium">{t('clubs.form.descriptionOptional')}</FormLabel>
                            <FormControl>
                              <Textarea
                                data-testid="input-club-description"
                                placeholder={t('clubs.form.descriptionPlaceholder')}
                                className="bg-gray-50/80 dark:bg-input border-gray-200 dark:border-border focus:bg-white transition-colors min-h-[80px] resize-none text-sm"
                                {...field}
                                value={field.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="settings" className="bg-white dark:bg-card border border-gray-200 dark:border-border shadow-sm rounded-xl overflow-hidden">
                    <AccordionTrigger className="bg-gray-50/80 dark:bg-transparent hover:bg-gray-100 dark:hover:bg-muted/30 hover:no-underline px-4 py-3 text-base font-semibold text-gray-800 dark:text-foreground data-[state=open]:border-b data-[state=open]:dark:bg-muted/40 border-gray-100 dark:border-border transition-colors m-0">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-500" />
                        Paramètres du Club
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-5 p-4 bg-white dark:bg-card">
                      {/* Validation Method Setting */}
                      <FormField
                        control={createClubForm.control}
                        name="validationMethod"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-muted-foreground font-medium text-sm">
                              {t('clubs.form.validationMethod')}
                            </FormLabel>
                            <FormControl>
                              <div className="flex bg-gray-100/50 dark:bg-muted p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => field.onChange("auto")}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${field.value === "auto"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.newPlayersAuto')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("standard")}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${field.value === "standard"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.standard')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("admin_only")}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${field.value === "admin_only"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  Admins
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* New Player Validation Setting */}
                      <FormField
                        control={createClubForm.control}
                        name="newPlayerValidation"
                        render={({ field }) => (
                          <FormItem className="space-y-3 pt-4 border-t border-border">
                            <FormLabel className="text-muted-foreground font-medium text-sm">
                              {t('clubs.form.newPlayerValidation')}
                            </FormLabel>
                            <FormControl>
                              <div className="flex bg-gray-100/50 dark:bg-muted p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => field.onChange("auto")}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${field.value === "auto"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.newPlayersAuto')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("admins")}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${field.value === "admins"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.newPlayersAdmins')}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* ELO Starting Setting */}
                      <FormField
                        control={createClubForm.control}
                        name="eloStarting"
                        render={({ field }) => (
                          <FormItem className="space-y-3 pt-4 border-t border-border">
                            <FormLabel className="text-muted-foreground font-medium text-sm flex items-center justify-between">
                              {t('clubs.form.eloStarting')}
                            </FormLabel>
                            <FormControl>
                              <div className="flex bg-gray-100/50 dark:bg-muted p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => field.onChange("standard")}
                                  className={`flex-1 py-2 px-2 text-sm font-medium rounded-md transition-all ${field.value === "standard"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.form.eloStartingStandard')} <span className="text-xs text-muted-foreground font-normal">(1500)</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("global")}
                                  className={`flex-1 py-2 px-2 text-sm font-medium rounded-md transition-all ${field.value === "global"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.form.eloStartingGlobal').replace("Global", "").replace("(", "").replace(")", "").trim()}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Bottom Actions Bar */}
              <div className="pt-4 mt-4 border-t border-gray-200 dark:border-border flex items-center justify-between gap-3 bg-white dark:bg-card flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  className="font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  {t('clubs.create.cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px] shadow-sm ml-auto dark-btn-active"
                  data-testid="button-submit-club"
                  disabled={createClubMutation.isPending}
                >
                  {createClubMutation.isPending ? t('clubs.create.creating') : t('clubs.create.submit')}
                </Button>
              </div>
            </form>
          </Form>
        </ResponsiveModal>

        {/* Edit Club Dialog */}
        <ResponsiveModal
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        >
          <Form {...editClubForm}>
            <form onSubmit={onEditClub} className="flex flex-col max-h-[calc(100vh-10rem)] sm:max-h-[75vh]">
              <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 pb-2 space-y-4">
                <Accordion type="single" collapsible defaultValue="general" className="w-full space-y-4">
                  <AccordionItem value="general" className="bg-white dark:bg-card border border-gray-200 dark:border-border shadow-sm rounded-xl overflow-hidden">
                    <AccordionTrigger className="bg-gray-50/80 dark:bg-transparent hover:bg-gray-100 dark:hover:bg-muted/30 hover:no-underline px-4 py-3 text-base font-semibold text-gray-800 dark:text-foreground data-[state=open]:border-b data-[state=open]:dark:bg-muted/40 border-gray-100 dark:border-border transition-colors m-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        Informations Générales
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 p-4 bg-white dark:bg-card">
                      <FormField
                        control={editClubForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel className="text-muted-foreground font-medium">{t('clubs.form.name')}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter club name"
                                maxLength={25}
                                className="bg-gray-50/80 dark:bg-input border-gray-200 dark:border-border focus:bg-white transition-colors"
                                {...field}
                                data-testid="input-edit-club-name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editClubForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel className="text-muted-foreground font-medium">{t('clubs.form.description')}</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={t('clubs.form.descriptionPlaceholder')}
                                className="bg-gray-50/80 dark:bg-input border-gray-200 dark:border-border focus:bg-white transition-colors min-h-[80px] resize-none text-sm"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-edit-club-description"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="settings" className="bg-white dark:bg-card border border-gray-200 dark:border-border shadow-sm rounded-xl overflow-hidden">
                    <AccordionTrigger className="bg-gray-50/80 dark:bg-transparent hover:bg-gray-100 dark:hover:bg-muted/30 hover:no-underline px-4 py-3 text-base font-semibold text-gray-800 dark:text-foreground data-[state=open]:border-b data-[state=open]:dark:bg-muted/40 border-gray-100 dark:border-border transition-colors m-0">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-500" />
                        Paramètres du Club
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-5 p-4 bg-white dark:bg-card">
                      {/* Validation Method Setting */}
                      <FormField
                        control={editClubForm.control}
                        name="validationMethod"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-muted-foreground font-medium text-sm">
                              {t('clubs.form.validationMethod')}
                            </FormLabel>
                            <FormControl>
                              <div className="flex bg-gray-100/50 dark:bg-muted p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => field.onChange("auto")}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${field.value === "auto"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.newPlayersAuto')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("standard")}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${field.value === "standard"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.standard')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("admin_only")}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${field.value === "admin_only"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  Admins
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* New Player Validation Setting */}
                      <FormField
                        control={editClubForm.control}
                        name="newPlayerValidation"
                        render={({ field }) => (
                          <FormItem className="space-y-3 pt-4 border-t border-border">
                            <FormLabel className="text-muted-foreground font-medium text-sm">
                              {t('clubs.form.newPlayerValidation')}
                            </FormLabel>
                            <FormControl>
                              <div className="flex bg-gray-100/50 dark:bg-muted p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => field.onChange("auto")}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${field.value === "auto"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.newPlayersAuto')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("admins")}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${field.value === "admins"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.validation.newPlayersAdmins')}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* ELO Starting Setting */}
                      <FormField
                        control={editClubForm.control}
                        name="eloStarting"
                        render={({ field }) => (
                          <FormItem className="space-y-3 pt-4 border-t border-border">
                            <FormLabel className="text-muted-foreground font-medium text-sm flex items-center justify-between">
                              {t('clubs.form.eloStarting')}
                            </FormLabel>
                            <FormControl>
                              <div className="flex bg-gray-100/50 dark:bg-muted p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => field.onChange("standard")}
                                  className={`flex-1 py-2 px-2 text-sm font-medium rounded-md transition-all ${field.value === "standard"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.form.eloStartingStandard')} <span className="text-xs text-muted-foreground font-normal">(1500)</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange("global")}
                                  className={`flex-1 py-2 px-2 text-sm font-medium rounded-md transition-all ${field.value === "global"
                                    ? "bg-white dark:bg-background text-blue-600 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark-btn-active"
                                    : "text-gray-600 dark:text-foreground/50 hover:text-gray-900 dark:hover:text-foreground"
                                    }`}
                                >
                                  {t('clubs.form.eloStartingGlobal').replace("Global", "").replace("(", "").replace(")", "").trim()}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Club Actions - Actions Dangereuses */}
                      <div className="pt-6 mt-6 border-t border-gray-100 dark:border-border flex flex-col sm:flex-row gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 border-orange-200 text-orange-600 hover:bg-orange-50 bg-white dark:bg-card shadow-sm"
                          title={t('clubs.actions.resetSeason', { defaultValue: 'Reset Season' })}
                          onClick={() => {
                            setClubToResetSeason(editingClub?.id || null);
                            setResetSeasonConfirmOpen(true);
                          }}
                          disabled={resetSeasonMutation.isPending}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          <span>Nouvelle Saison</span>
                        </Button>

                        <Button
                          type="button"
                          variant="ghost"
                          className="flex-1 text-red-500 hover:text-red-600 hover:bg-red-50 font-medium border border-red-100/50"
                          onClick={() => {
                            if (!currentPlayer?.isPremium) {
                              showPremiumRestriction("Deleting");
                              return;
                            }
                            if (editingClub && confirm(`Voulez-vous vraiment supprimer définitivement le club "${editingClub.name}" ?`)) {
                              deleteClubMutation.mutate(editingClub.id);
                            }
                          }}
                          disabled={deleteClubMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {deleteClubMutation.isPending ? t('clubs.edit.deleting') : "Supprimer le Club"}
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="members" className="bg-white dark:bg-card border border-gray-200 dark:border-border shadow-sm rounded-xl overflow-hidden">
                    <AccordionTrigger className="bg-gray-50/80 dark:bg-transparent hover:bg-gray-100 dark:hover:bg-muted/30 hover:no-underline px-4 py-3 text-base font-semibold text-gray-800 dark:text-foreground data-[state=open]:border-b data-[state=open]:dark:bg-muted/40 border-gray-100 dark:border-border transition-colors m-0">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-green-500" />
                        {t('clubs.form.manageMembers')}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 p-4 bg-white dark:bg-card">
                      {/* Player Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          placeholder={t('clubs.form.searchPlayers')}
                          value={playerSearchQuery}
                          onChange={(e) => setPlayerSearchQuery(e.target.value)}
                          className="pl-10 bg-gray-50/80 dark:bg-input border-gray-100 dark:border-border focus:bg-white dark:focus:bg-input transition-colors"
                          data-testid="input-search-edit-players"
                        />
                      </div>

                      <div className="max-h-[300px] overflow-y-auto rounded-lg border border-gray-100 dark:border-border bg-gray-50/30 dark:bg-muted/30 p-1.5 space-y-1.5">
                        {(() => {
                          // Only show current members (selected or pending) — new players must join on their own.
                          const displayPlayers = sortedPlayers.filter((p) =>
                            selectedPlayers.includes(p.id) || pendingPlayerIds.has(p.id)
                          );

                          return displayPlayers.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-6">
                              {playerSearchQuery ? t('clubs.messages.noPlayersFound') : t('clubs.messages.noPlayersAvailable')}
                            </p>
                          ) : (
                            displayPlayers.map((player) => {
                              const isSelected = selectedPlayers.includes(player.id);
                              const isPending = pendingPlayerIds.has(player.id);
                              const isAdmin = selectedAdmins.includes(player.id);

                              return (
                                <div
                                  key={player.id}
                                  className={`flex items-center justify-between p-2.5 rounded-lg border shadow-sm transition-all group ${isSelected || isPending ? 'bg-white dark:bg-card border-green-100/50' : 'bg-white/60 dark:bg-card/60 border-gray-100 dark:border-border hover:bg-white dark:hover:bg-card'
                                    }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex flex-col">
                                      <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                                        {player.name}
                                        {followedPlayers.has(player.id) && (
                                          <Star size={12} className="fill-current text-yellow-500" />
                                        )}
                                        {isPending && (
                                          <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1.5 rounded uppercase font-bold tracking-wider">
                                            Pending
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1">
                                    {isPending ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => togglePlayerSelection(player.id)}
                                          className="p-2 rounded-md text-green-600 bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors"
                                          title="Accepter le joueur"
                                        >
                                          <Check size={16} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => rejectPendingPlayer(player.id)}
                                          className="p-2 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                                          title="Refuser le joueur"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </>
                                    ) : !isSelected ? (
                                      <button
                                        type="button"
                                        onClick={() => togglePlayerSelection(player.id)}
                                        className="px-3 py-1.5 rounded-md text-xs font-medium text-padel-green bg-green-50 hover:bg-padel-green hover:text-white transition-colors flex items-center gap-1 border border-green-100 dark-btn-active"
                                        title="Add to club"
                                      >
                                        <UserPlus size={14} /> <span className="hidden sm:inline">Ajouter</span>
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => toggleAdminSelection(player.id)}
                                          className={`p-2 rounded-md transition-colors ${isAdmin
                                            ? 'text-yellow-500 fill-current bg-yellow-50 dark:bg-yellow-900/40 dark:text-yellow-400'
                                            : 'text-gray-300 hover:bg-yellow-50 hover:text-yellow-500 dark:text-gray-500 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400'
                                            }`}
                                          title={isAdmin ? 'Retirer les droits Admin' : 'Nommer Admin'}
                                          data-testid={`button-edit-admin-${player.id}`}
                                        >
                                          <Shield size={16} className={isAdmin ? 'fill-current' : ''} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => togglePlayerSelection(player.id)}
                                          className="p-2 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors opacity-100 sm:opacity-50 sm:group-hover:opacity-100"
                                          title="Retirer du club"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )
                            })
                          );
                        })()}
                      </div>
                      {selectedPlayers.length > 0 && (
                        <div className="text-xs text-muted-foreground font-medium px-1 flex items-center justify-between">
                          <span>{selectedPlayers.length} joueurs sélectionnés</span>
                          <span className="text-blue-600">{selectedAdmins.length} admins</span>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Bottom Actions Bar */}
              <div className="pt-4 mt-4 border-t border-gray-200 dark:border-border flex items-center justify-between gap-3 bg-white dark:bg-card flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  className="font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setEditDialogOpen(false);
                    setEditingClub(null);
                    setSelectedPlayers([]);
                    setSelectedAdmins([]);
                    setPlayerSearchQuery("");
                    editClubForm.reset();
                  }}
                >
                  {t('clubs.create.cancel')}
                </Button>

                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px] shadow-sm ml-auto dark-btn-active"
                  disabled={updateClubMutation.isPending}
                  data-testid="button-update-club"
                >
                  {updateClubMutation.isPending ? t('clubs.edit.updating') : t('clubs.edit.submit')}
                </Button>
              </div>
            </form>
          </Form>
      </ResponsiveModal>

      {/* Player Selection Banner */}
      {
        !selectedPlayerId && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-amber-800 text-sm">
              {t('clubs.messages.selectPlayer')}
            </p>
          </div>
        )
      }

      {/* Clubs List */}
      <div className="space-y-2">
        {filteredClubs.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            {searchQuery ? t('clubs.messages.noClubsFound') : t('clubs.messages.noClubsAvailable')}
          </div>
        ) : (
          filteredClubs.map((club) => (
            <div
              key={club.id}
              className={`bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-muted ${selectedClub === club.id ? 'border-padel-green dark:border-padel-green' : ''
                }`}
              onClick={() => handleClubClick(club.id)}
              data-testid={`card-club-${club.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-foreground">{club.name}</h3>
                      {club.isVerified && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <BadgeCheck className="w-5 h-5 text-blue-500 fill-blue-500/20" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t('clubs.messages.verifiedOfficialClub')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {club.isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClub(club);
                          }}
                          title={t('clubs.actions.edit', { defaultValue: 'Éditer le club' })}
                          className="flex items-center space-x-1 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors px-2 py-1 rounded-full font-medium dark-btn-active"
                          data-testid={`badge-edit-club-${club.id}`}
                        >
                          <Edit className="w-3 h-3" />
                          <span>Admin</span>
                        </button>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">


                    </div>
                  </div>

                  {club.description && (
                    <p className={`text-sm text-muted-foreground mb-2 ${selectedClub === club.id ? "" : "line-clamp-2"}`}>{club.description}</p>
                  )}

                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {club.memberCount}
                    </div>
                    <div className="flex items-center">
                      <span className="text-muted-foreground mr-1">•</span><Edit className="w-3 h-3 mr-1" />
                      <span>{club.createdByName?.split(' ')[0] || "Unknown"}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(club.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="ml-4 flex flex-col items-center space-y-2">
                  {selectedPlayerId && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-8 min-w-[2rem] px-0 transition-all duration-200 ${club.isMember
                        ? club.status === 'pending'
                          ? 'bg-yellow-50 dark:bg-yellow-900/40 border-yellow-200 dark:border-yellow-600 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-100 hover:border-yellow-300 w-auto px-2'
                          : 'bg-padel-green border-padel-green text-white hover:bg-padel-green dark-btn-active w-8'
                        : 'border-gray-300 dark:border-border text-muted-foreground hover:bg-yellow-400 hover:border-yellow-400 hover:text-white w-8'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (club.isMember) {
                          if (club.status === 'pending') {
                            return;
                          }
                          handleLeaveClub(club.id);
                        } else {
                          handleJoinClub(club.id);
                        }
                      }}
                      disabled={joinClubMutation.isPending || leaveClubMutation.isPending}
                      data-testid={`button-join-${club.id}`}
                    >
                      {club.isMember && club.status === 'pending' ? (
                        <span className="text-xs font-medium whitespace-nowrap">{t('clubs.actions.pending')}</span>
                      ) : (
                        <UserPlus className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => handleShareClub(club, e)}
                    className="text-gray-500 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground hover:bg-gray-100 dark:hover:bg-muted mt-1"
                    data-testid={`button-share-club-${club.id}`}
                    title={t('clubs.actions.share', { defaultValue: 'Partager le club' })}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Club Rankings - Only displayed when selected */}
              {selectedClub === club.id && <ClubRankings clubId={club.id} />}
            </div>
          ))
        )}
      </div>

      {/* Leave Club Confirmation Dialog */}
      <AlertDialog open={leaveClubDialogOpen} onOpenChange={setLeaveClubDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clubs.messages.leaveWarningTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('clubs.messages.leaveWarningDesc')}
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Your club ELO rating and progression</li>
                <li>Match history within this club</li>
                <li>Win/loss statistics for this club</li>
                <li>Ranking position and achievements</li>
              </ul>
              <br />
              This action cannot be undone. You can rejoin the club later, but you'll start fresh with default ratings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLeaveClub}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Leave Club
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Removal Dialog */}
      <AlertDialog open={confirmRemovalDialogOpen} onOpenChange={setConfirmRemovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Warning: Removing Members
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to remove players from this club.
              </p>
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm font-medium">
                Their "All Time" statistics and ranking history in this club will be permanently deleted.
              </div>
              <p>
                This action cannot be undone. Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setConfirmRemovalDialogOpen(false);
              setPendingEditData(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemoval}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove & Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Season Confirmation Dialog */}
      <AlertDialog open={resetSeasonConfirmOpen} onOpenChange={setResetSeasonConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              {t('clubs.actions.resetSeason', { defaultValue: 'Reset Season' })}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>{t('clubs.messages.confirmResetSeason', { defaultValue: 'Are you sure you want to reset the current season? All current rankings will be archived and members will start a new season.' })}</p>
              <div className="bg-orange-50 border border-orange-200 rounded p-3 text-orange-800 text-sm font-medium">
                {t('clubs.messages.confirmResetSeasonWarning', { defaultValue: 'This operation is irreversible. Current ELO scores will be archived and reset.' })}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setResetSeasonConfirmOpen(false);
              setClubToResetSeason(null);
            }}>
              {t('clubs.create.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (clubToResetSeason) {
                  resetSeasonMutation.mutate(clubToResetSeason);
                }
                setResetSeasonConfirmOpen(false);
                setClubToResetSeason(null);
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {t('clubs.actions.resetSeason', { defaultValue: 'Reset Season' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Input Restrictions Dialog */}
      <InputRestrictionsDialog
        open={showRestrictionsDialog}
        onOpenChange={setShowRestrictionsDialog}
        onConfirm={() => setShowRestrictionsDialog(false)}
        message={restrictionMessage}
      />

    </div>
  );
}